import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { todayLocalISO } from "../utils/date";
import { faRulerCombined, faPenToSquare, faTrashCan, faPlus, faTableList, faChartLine, faCamera, faUser, faChevronDown, faCircleInfo, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import styles from "./BodyMeasurementsModal.module.css";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";
import { BodyProgressContent } from "./BodyProgressModal";
import PhotoGuideModal from "./PhotoGuideModal";
import { themeColors } from "../utils/theme";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import api from "../services/api";
import { downloadPdf } from "../utils/downloadPdf";

// ─── helpers ────────────────────────────────────────────────────────────────
const today = () => todayLocalISO();

function fmtDate(d) {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

function indicator(label, value, unit, risk, riskColor, tooltip, wide) {
  return { label, value, unit, risk, riskColor, tooltip, wide };
}

function buildIndicators(ind, hideBmi = false) {
  if (!ind) return [];
  const list = [];
  const { bmi, bmi_category, body_fat_pct, body_fat_method, lean_mass_kg, fat_mass_kg,
          waist_hip_ratio, whr_risk, whr_classification, waist_height_ratio, whtr_risk,
          bmr_kcal, tdee_sedentary, tdee_moderate, tdee_active,
          bone_mass_kg, residual_mass_kg, muscle_mass_kg,
          arm_muscle_area_zscore, arm_muscle_area_classification, arm_muscle_area_disclaimer } = ind;

  if (bmi != null && !hideBmi) {
    const cat = bmi_category || "";
    const color = bmi < 18.5 ? "#63b3ed" : bmi < 25 ? "#9fe1cb" : bmi < 30 ? "var(--feedback-warning)" : "#ff6b6b";
    list.push(indicator("IMC", bmi, "", cat, color));
  }
  if (body_fat_pct != null) {
    const color = body_fat_pct < 15 ? "#63b3ed" : body_fat_pct < 25 ? "#9fe1cb" : body_fat_pct < 32 ? "var(--feedback-warning)" : "#ff6b6b";
    const label = body_fat_method ? `Gordura corporal (${body_fat_method})` : "Gordura corporal";
    list.push(indicator(label, body_fat_pct, "%", null, color));
  }
  if (lean_mass_kg != null) list.push(indicator("Massa magra", lean_mass_kg, "kg", null, "#9fe1cb"));
  if (fat_mass_kg != null)  list.push(indicator("Massa gorda", fat_mass_kg, "kg", null, "var(--feedback-warning)"));
  if (waist_hip_ratio != null) {
    const color = whr_risk === "Alto" ? "#ff6b6b" : "#9fe1cb";
    // whr_classification é a tabela por faixa etária/sexo da planilha do
    // professor (mais granular); whr_risk continua sendo o critério binário
    // da OMS já existente — mostro os dois quando disponíveis.
    const label = whr_classification ? whr_classification : whr_risk;
    list.push(indicator("Rel. cintura/quadril (RCQ)", waist_hip_ratio, "", label, color));
  }
  if (waist_height_ratio != null) {
    const color = whtr_risk === "Alto" ? "#ff6b6b" : "#9fe1cb";
    list.push(indicator("Rel. cintura/altura", waist_height_ratio, "", whtr_risk, color));
  }
  if (bmr_kcal != null)      list.push(indicator("TMB (kcal/dia)", bmr_kcal, "kcal", null, "var(--text-primary)"));
  if (tdee_sedentary != null) list.push(indicator("Gasto sedentário", tdee_sedentary, "kcal", null, "var(--text-primary)"));
  if (tdee_moderate != null)  list.push(indicator("Gasto moderado", tdee_moderate, "kcal", null, "var(--text-primary)"));
  if (tdee_active != null)    list.push(indicator("Gasto ativo", tdee_active, "kcal", null, "var(--text-primary)"));
  // Composição corporal em 4 componentes (Frente C) — só aparece quando os
  // diâmetros ósseos foram preenchidos.
  if (bone_mass_kg != null) list.push(indicator("Massa óssea", bone_mass_kg, "kg", null, "#9fe1cb"));
  if (residual_mass_kg != null) list.push(indicator("Massa residual", residual_mass_kg, "kg", null, "#9fe1cb"));
  if (muscle_mass_kg != null) list.push(indicator("Massa muscular", muscle_mass_kg, "kg", null, "#63b3ed"));
  // Área muscular do braço — aviso de fonte não confirmada vira ícone de
  // info com tooltip (mesmo padrão de WorkoutAdminModal::infoTooltip*) em vez
  // de texto concatenado no card, que poluía o indicador.
  if (arm_muscle_area_zscore != null) {
    list.push(indicator(
      "Área muscular do braço (escore-Z)", arm_muscle_area_zscore, "",
      arm_muscle_area_classification, "var(--feedback-warning)",
      arm_muscle_area_disclaimer || null,
      true, // título longo + classificação: ocupa 2 colunas pra não esticar a linha
    ));
  }
  return list;
}

// ─── R362: prévia ao vivo (espelha app/api/body_measurements.py::compute_indicators) ──
// Não existe endpoint de preview sem persistir — replicar a fórmula aqui evita
// round-trip ao servidor a cada tecla, mas tem risco de desalinhar do backend
// com o tempo. Qualquer mudança em compute_indicators() no backend precisa ser
// replicada aqui também.
function computeIndicatorsClient(form) {
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  const h = num(form.height_cm), w = num(form.weight_kg);
  const waist = num(form.waist_cm), hip = num(form.hip_cm), neck = num(form.neck_cm);
  const age = num(form.age);
  const gender = (form.gender || "").toUpperCase();
  const ind = {};

  if (h && w && h > 0) {
    const bmi = w / ((h / 100) ** 2);
    ind.bmi = bmi;
    ind.bmi_category = bmi < 18.5 ? "Abaixo do peso" : bmi < 25 ? "Peso normal" : bmi < 30 ? "Sobrepeso"
      : bmi < 35 ? "Obesidade grau I" : bmi < 40 ? "Obesidade grau II" : "Obesidade grau III";
  }

  const protocol = form.skinfold_protocol || "jackson_pollock_7";
  const abdomen = num(form.abdomen_cm);

  if (protocol === "weltman") {
    // Weltman (1987) — usa Abdômen (distinto de Cintura), sem dobra cutânea.
    if (abdomen && h && w && (gender === "M" || gender === "F")) {
      ind.body_fat_pct = gender === "F"
        ? 0.11077 * abdomen - 0.17666 * h + 0.187 * w + 51.03301
        : 0.31457 * abdomen - 0.10969 * w + 10.8336;
      ind.body_fat_method = "Weltman";
    }
  } else if (protocol === "deurenberg") {
    // Deurenberg et al. (1991) — usa IMC, sem dobra cutânea nem perímetro.
    if (ind.bmi != null && age && (gender === "M" || gender === "F")) {
      const sexo = gender === "M" ? 1 : 0;
      ind.body_fat_pct = 1.2 * ind.bmi + 0.23 * age - 10.8 * sexo - 5.4;
      ind.body_fat_method = "Deurenberg";
    }
  } else if (protocol === "durnin_womersley") {
    // Durnin & Womersley (1974) — Tricipital+Bicipital+Subescapular+Supra-ilíaca,
    // coeficiente por faixa etária (mesma tabela do backend).
    const sf = [num(form.sf_tricep_mm), num(form.sf_biceps_mm), num(form.sf_subscapular_mm), num(form.sf_suprailiac_mm)];
    if (sf.every(v => v != null && v > 0) && age && (gender === "M" || gender === "F")) {
      const S = sf.reduce((a, b) => a + b, 0);
      const bands = [
        [16, 1.1533, 0.0643, 1.1369, 0.0598],
        [19, 1.1620, 0.0630, 1.1549, 0.0678],
        [29, 1.1631, 0.0632, 1.1599, 0.0717],
        [39, 1.1422, 0.0544, 1.1423, 0.0632],
        [49, 1.1620, 0.0700, 1.1333, 0.0612],
        [Infinity, 1.1715, 0.0779, 1.1339, 0.0645],
      ];
      const [, aM, bM, aF, bF] = bands.find(([maxAge]) => age <= maxAge);
      const density = gender === "F" ? (aF - bF * Math.log10(S)) : (aM - bM * Math.log10(S));
      ind.body_fat_pct = (4.95 / density - 4.50) * 100;
      ind.body_fat_method = "Durnin & Womersley";
    }
  } else if (protocol === "faulkner") {
    // Faulkner — %G direto, sem densidade/Siri. Mesma fórmula pros 2 sexos.
    const sf = [num(form.sf_tricep_mm), num(form.sf_subscapular_mm), num(form.sf_suprailiac_mm), num(form.sf_abdominal_mm)];
    if (sf.every(v => v != null && v > 0)) {
      const S = sf.reduce((a, b) => a + b, 0);
      ind.body_fat_pct = S * 0.153 + 5.783;
      ind.body_fat_method = "Faulkner";
    }
  } else if (protocol === "guedes_3") {
    // Guedes 3 dobras — combinação de dobras diferente por sexo.
    const sf = gender === "F"
      ? [num(form.sf_thigh_mm), num(form.sf_suprailiac_mm), num(form.sf_subscapular_mm)]
      : gender === "M" ? [num(form.sf_tricep_mm), num(form.sf_suprailiac_mm), num(form.sf_abdominal_mm)] : [];
    if (sf.length && sf.every(v => v != null && v > 0)) {
      const S = sf.reduce((a, b) => a + b, 0);
      const density = gender === "F" ? (1.1665 - 0.07063 * Math.log10(S)) : (1.17136 - 0.06706 * Math.log10(S));
      ind.body_fat_pct = (4.95 / density - 4.50) * 100;
      ind.body_fat_method = "Guedes 3 dobras";
    }
  } else if (protocol === "jackson_pollock_3") {
    // Jackson & Pollock 3 dobras — combinação de dobras diferente por sexo.
    const sf = gender === "F"
      ? [num(form.sf_tricep_mm), num(form.sf_suprailiac_mm), num(form.sf_thigh_mm)]
      : gender === "M" ? [num(form.sf_chest_mm), num(form.sf_abdominal_mm), num(form.sf_thigh_mm)] : [];
    if (sf.length && sf.every(v => v != null && v > 0) && age) {
      const S = sf.reduce((a, b) => a + b, 0);
      const density = gender === "F"
        ? 1.0994921 - 0.0009929 * S + 0.0000023 * S ** 2 - 0.0001392 * age
        : 1.10938 - 0.0008267 * S + 0.0000016 * S ** 2 - 0.0002574 * age;
      ind.body_fat_pct = (4.95 / density - 4.50) * 100;
      ind.body_fat_method = "Jackson e Pollock 3 dobras";
    }
  } else if (protocol === "petroski") {
    // Petroski (1995) — combinação de dobras diferente por sexo.
    const sf = gender === "F"
      ? [num(form.sf_axillary_mm), num(form.sf_thigh_mm), num(form.sf_suprailiac_mm), num(form.sf_calf_mm)]
      : gender === "M" ? [num(form.sf_subscapular_mm), num(form.sf_tricep_mm), num(form.sf_suprailiac_mm), num(form.sf_calf_mm)] : [];
    if (sf.length && sf.every(v => v != null && v > 0) && age) {
      const S = sf.reduce((a, b) => a + b, 0);
      const density = gender === "F"
        ? 1.0346585 - 0.00063129 * S + 0.00000187 * S ** 2 - 0.000311 * age - 0.0004889 * w + 0.00051345 * h
        : 1.10726863 - 0.00081201 * S + 0.00000212 * S ** 2 - 0.00041761 * age;
      ind.body_fat_pct = (4.95 / density - 4.50) * 100;
      ind.body_fat_method = "Petroski";
    }
  } else if (protocol === "slaughter") {
    // Slaughter et al. (1988) — Tricipital+Panturrilha, %G direto.
    const sf = [num(form.sf_tricep_mm), num(form.sf_calf_mm)];
    if (sf.every(v => v != null && v > 0) && (gender === "M" || gender === "F")) {
      const S = sf.reduce((a, b) => a + b, 0);
      ind.body_fat_pct = gender === "F" ? (0.61 * S + 5.1) : (0.735 * S + 1.0);
      ind.body_fat_method = "Slaughter";
    }
  } else {
    // jackson_pollock_7 (padrão/legado) — Pollock 7 dobras preferencial,
    // U.S. Navy como fallback. Mesmos coeficientes do backend (conferidos
    // contra a publicação original — ver spec_avaliacao_fisica_v2.md §4).
    const sfKeys = ["sf_tricep_mm", "sf_subscapular_mm", "sf_chest_mm", "sf_axillary_mm", "sf_suprailiac_mm", "sf_abdominal_mm", "sf_thigh_mm"];
    const sfVals = sfKeys.map(k => num(form[k]));
    const hasSkinfolds = sfVals.every(v => v != null && v > 0);

    if (hasSkinfolds && age && (gender === "M" || gender === "F")) {
      const S = sfVals.reduce((a, b) => a + b, 0);
      const density = gender === "F"
        ? 1.097 - 0.00046971 * S + 0.00000056 * S ** 2 - 0.00012828 * age
        : 1.112 - 0.00043499 * S + 0.00000055 * S ** 2 - 0.00028826 * age;
      if (density) {
        ind.body_fat_pct = (4.95 / density - 4.50) * 100;
        ind.body_fat_method = "Pollock 7 dobras";
      }
    } else if (h && waist && neck && h > 0) {
      if (gender === "M" && waist > neck) {
        ind.body_fat_pct = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(h)) - 450;
        ind.body_fat_method = "U.S. Navy";
      } else if (gender === "F" && hip && (waist + hip) > neck) {
        ind.body_fat_pct = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(h)) - 450;
        ind.body_fat_method = "U.S. Navy";
      }
    }
  }

  if (w && Number.isFinite(ind.body_fat_pct)) {
    const fatKg = w * ind.body_fat_pct / 100;
    ind.fat_mass_kg = fatKg;
    ind.lean_mass_kg = w - fatKg;
  }

  if (waist && hip && hip > 0) {
    const whr = waist / hip;
    ind.waist_hip_ratio = whr;
    if (gender === "M") ind.whr_risk = whr >= 0.90 ? "Alto" : "Normal";
    else if (gender === "F") ind.whr_risk = whr >= 0.85 ? "Alto" : "Normal";

    // RCQ — classificação por faixa etária/sexo da planilha do professor
    // (mais granular que o whr_risk acima, que é o critério binário da OMS
    // já existente). Mesma tabela do backend.
    const whrBands = {
      "20-29": { F: [0.71, 0.78, 0.83], M: [0.83, 0.89, 0.95] },
      "30-39": { F: [0.72, 0.79, 0.85], M: [0.84, 0.92, 0.96] },
      "40-49": { F: [0.73, 0.80, 0.88], M: [0.88, 0.96, 1.01] },
      "50-59": { F: [0.74, 0.82, 0.89], M: [0.90, 0.97, 1.03] },
      "60-69": { F: [0.76, 0.84, 0.91], M: [0.91, 0.99, 1.04] },
    };
    const band = age != null
      ? Object.entries(whrBands).find(([range]) => {
          const [min, max] = range.split("-").map(Number);
          return age >= min && age <= max;
        })
      : null;
    if (band && (gender === "M" || gender === "F")) {
      const [mod, alto, muitoAlto] = band[1][gender];
      ind.whr_classification = whr < mod ? "Baixo" : whr < alto ? "Moderado" : whr < muitoAlto ? "Alto" : "Muito alto";
    }
  }

  if (waist && h && h > 0) {
    const whtr = waist / h;
    ind.waist_height_ratio = whtr;
    ind.whtr_risk = whtr >= 0.5 ? "Alto" : "Normal";
  }

  if (w && h && age) {
    let bmr = null;
    if (gender === "M") bmr = 10 * w + 6.25 * h - 5 * age + 5;
    else if (gender === "F") bmr = 10 * w + 6.25 * h - 5 * age - 161;
    if (bmr) {
      ind.bmr_kcal = bmr;
      ind.tdee_sedentary = bmr * 1.2;
      ind.tdee_moderate = bmr * 1.55;
      ind.tdee_active = bmr * 1.725;
    }
  }

  // Assimetria de membros (D/E) — mesmo limiar de ±1,5cm da planilha do professor
  // (ver app/api/body_measurements.py::compute_indicators, fonte da verdade)
  const ASYMMETRY_THRESHOLD_CM = 1.5;
  const limbPairs = [
    ["Braço", num(form.arm_cm_right), num(form.arm_cm_left)],
    ["Antebraço", num(form.forearm_cm_right), num(form.forearm_cm_left)],
    ["Coxa", num(form.thigh_cm_right), num(form.thigh_cm_left)],
    ["Panturrilha", num(form.calf_cm_right), num(form.calf_cm_left)],
  ];
  ind.limb_asymmetry = limbPairs
    .filter(([, r, l]) => r != null && l != null)
    .map(([limb, r, l]) => ({
      limb, right_cm: r, left_cm: l,
      diff_cm: Math.abs(r - l),
      significant: Math.abs(r - l) >= ASYMMETRY_THRESHOLD_CM,
    }));

  // Composição corporal em 4 componentes (Frente C) — Peso = Gorda + Óssea +
  // Residual + Muscular. Massa Gorda reaproveita fat_mass_kg (já calculado
  // acima). Massa Óssea usa a fórmula de Rocha (diâmetros em metros). Só
  // calculado com os diâmetros ósseos preenchidos.
  const wristDiam = num(form.bone_diam_wrist_cm), femurDiam = num(form.bone_diam_femur_cm);
  if (w && h && h > 0 && wristDiam && femurDiam && (gender === "M" || gender === "F") && ind.fat_mass_kg != null) {
    const hM = h / 100;
    const boneMass = 3.02 * ((hM ** 2) * (wristDiam / 100) * (femurDiam / 100) * 400) ** 0.712;
    const residualMass = gender === "F" ? w * 0.209 : w * 0.241;
    ind.bone_mass_kg = boneMass;
    ind.residual_mass_kg = residualMass;
    ind.muscle_mass_kg = w - (ind.fat_mass_kg + residualMass + boneMass);
  }

  // Área muscular do braço — escore-Z (Frente C). ⚠️ Metodologia sem fonte
  // científica confirmada (nem o professor sabe a origem das constantes de
  // referência) — replicada da planilha dele porque é usada na prática, mas
  // marcada com arm_muscle_area_disclaimer pra não passar mais certeza do
  // que existe. Só braço — a versão da coxa tinha uma inconsistência de
  // unidade não resolvida e ficou de fora (ver spec_avaliacao_fisica_v2.md).
  const armR = num(form.arm_cm_right), tricepSf = num(form.sf_tricep_mm);
  if (armR && tricepSf && h && h > 0) {
    const correctedArmCirc = armR - (Math.PI * (tricepSf / 10));
    const z = (correctedArmCirc * (170.18 / h) - 24.78) / 4.31;
    ind.arm_muscle_area_zscore = z;
    ind.arm_muscle_area_classification =
      z > 3 ? "Excelente volume muscular" :
      z > 2 ? "Ótimo volume muscular" :
      z > 1 ? "Bom volume muscular" :
      z > -1 ? "Volume muscular adequado" :
      z > -2 ? "Déficit muscular leve" :
      z > -3 ? "Déficit muscular moderado" : "Déficit muscular severo";
    ind.arm_muscle_area_disclaimer = "Metodologia sem fonte científica confirmada";
  }

  for (const k of Object.keys(ind)) {
    const v = ind[k];
    if (typeof v === "number" && Number.isFinite(v)) ind[k] = Math.round(v * 100) / 100;
  }
  return ind;
}

const EMPTY_FORM = {
  date: today(),
  height_cm: "", weight_kg: "", age: "", gender: "",
  waist_cm: "", abdomen_cm: "", hip_cm: "", neck_cm: "", chest_cm: "",
  arm_cm_right: "", arm_cm_left: "",
  forearm_cm_right: "", forearm_cm_left: "",
  thigh_cm_right: "", thigh_cm_left: "",
  calf_cm_right: "", calf_cm_left: "",
  skinfold_protocol: "",
  sf_tricep_mm: "", sf_subscapular_mm: "", sf_chest_mm: "",
  sf_axillary_mm: "", sf_suprailiac_mm: "", sf_abdominal_mm: "", sf_thigh_mm: "",
  sf_biceps_mm: "", sf_calf_mm: "",
  bone_diam_humeral_cm: "", bone_diam_wrist_cm: "", bone_diam_femur_cm: "",
  systolic_bp: "", diastolic_bp: "", resting_heart_rate: "",
};

// ─── R365: autofill de altura/idade/sexo a partir do cadastro ──────────────
// User.gender usa vocabulário "masculino"/"feminino"/"outro"/"prefiro_nao_informar"
// (app/models/user.py) enquanto o form de medidas usa "M"/"F" (compute_indicators
// só ramifica nesses dois valores) — sem mapear, "outro"/"prefiro_nao_informar"
// ficam de fora do autofill de propósito, não dá pra inferir a fórmula certa.
function mapGenderToForm(gender) {
  if (gender === "masculino") return "M";
  if (gender === "feminino") return "F";
  return "";
}

// User não guarda idade diretamente, só birth_date — calcula em anos completos.
function calcAgeFromBirthDate(birthDate) {
  if (!birthDate) return null;
  const b = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const beforeBirthdayThisYear = t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate());
  if (beforeBirthdayThisYear) age--;
  return age >= 0 ? age : null;
}

// Só preenche o que já existe no cadastro — nunca sobrescreve com vazio,
// então mesclar com EMPTY_FORM (spread) preserva os demais campos em branco.
function autofillFromProfile(profile) {
  if (!profile) return {};
  const out = {};
  if (profile.height_cm != null) out.height_cm = String(profile.height_cm);
  const age = calcAgeFromBirthDate(profile.birth_date);
  if (age != null) out.age = String(age);
  const gender = mapGenderToForm(profile.gender);
  if (gender) out.gender = gender;
  return out;
}

const REQUIRED_SKINFOLDS_BY_PROTOCOL = {
  durnin_womersley: { any: ["sf_tricep_mm", "sf_biceps_mm", "sf_subscapular_mm", "sf_suprailiac_mm"] },
  faulkner: { any: ["sf_tricep_mm", "sf_subscapular_mm", "sf_suprailiac_mm", "sf_abdominal_mm"] },
  guedes_3: { F: ["sf_thigh_mm", "sf_suprailiac_mm", "sf_subscapular_mm"], M: ["sf_tricep_mm", "sf_suprailiac_mm", "sf_abdominal_mm"] },
  jackson_pollock_3: { F: ["sf_tricep_mm", "sf_suprailiac_mm", "sf_thigh_mm"], M: ["sf_chest_mm", "sf_abdominal_mm", "sf_thigh_mm"] },
  petroski: { F: ["sf_axillary_mm", "sf_thigh_mm", "sf_suprailiac_mm", "sf_calf_mm"], M: ["sf_subscapular_mm", "sf_tricep_mm", "sf_suprailiac_mm", "sf_calf_mm"] },
  slaughter: { any: ["sf_tricep_mm", "sf_calf_mm"] },
};
function isSkinfoldRequired(fieldKey, form) {
  const entry = REQUIRED_SKINFOLDS_BY_PROTOCOL[form.skinfold_protocol];
  if (!entry) return false;
  const keys = entry.any || entry[(form.gender || "").toUpperCase()] || [];
  return keys.includes(fieldKey);
}

const FIELD_GROUPS = [
  {
    title: "Dados básicos",
    fields: [
      { key: "date",      label: "Data",         type: "date",   required: true },
      { key: "weight_kg", label: "Peso",          unit: "kg",     required: true },
      { key: "height_cm", label: "Altura",        unit: "cm",     required: true },
      { key: "age",       label: "Idade",         unit: "anos", step: "1" },
      { key: "gender",    label: "Sexo",          type: "select", options: [{ v: "", l: "-" }, { v: "M", l: "Masculino" }, { v: "F", l: "Feminino" }] },
    ],
  },
  {
    title: "Circunferências",
    fields: [
      { key: "waist_cm",   label: "Cintura",  unit: "cm" },
      { key: "abdomen_cm", label: "Abdômen",  unit: "cm", requiredIf: (f) => f.skinfold_protocol === "weltman" },
      { key: "hip_cm",    label: "Quadril",       unit: "cm" },
      { key: "neck_cm",   label: "Pescoço",       unit: "cm" },
      { key: "chest_cm",  label: "Peito",         unit: "cm" },
      { key: "arm_cm_right",     label: "Braço (D)",        unit: "cm" },
      { key: "arm_cm_left",      label: "Braço (E)",        unit: "cm" },
      { key: "forearm_cm_right", label: "Antebraço (D)",    unit: "cm" },
      { key: "forearm_cm_left",  label: "Antebraço (E)",    unit: "cm" },
      { key: "thigh_cm_right",   label: "Coxa (D)",         unit: "cm" },
      { key: "thigh_cm_left",    label: "Coxa (E)",         unit: "cm" },
      { key: "calf_cm_right",    label: "Panturrilha (D)",  unit: "cm" },
      { key: "calf_cm_left",     label: "Panturrilha (E)",  unit: "cm" },
    ],
  },
  {
    title: "Protocolo de gordura corporal",
    subtitle: "Weltman e Deurenberg não usam dobra cutânea - úteis quando não dá pra aferir (ex: sobrepeso alto)",
    // R: decisão de protocolo é relevante demais pra ficar escondida atrás
    // de um toggle (era collapsible quando só existia Pollock 7 opcional).
    // Ordem das opções replica exatamente a planilha original do professor
    // (célula de validação de dados, ver spec_avaliacao_fisica_v2.md §Frente B).
    fields: [
      {
        key: "skinfold_protocol", label: "Protocolo", type: "select",
        options: [
          { v: "deurenberg",         l: "Deurenberg - sem dobra (usa IMC)" },
          { v: "durnin_womersley",   l: "Durnin & Womersley (16 a 72 anos)" },
          { v: "faulkner",           l: "Faulkner" },
          { v: "guedes_3",           l: "Guedes 3 dobras" },
          { v: "jackson_pollock_3",  l: "Jackson e Pollock 3 dobras (18 a 61 anos)" },
          { v: "jackson_pollock_7",  l: "Jackson e Pollock 7 dobras (18 a 61 anos)" },
          { v: "petroski",           l: "Petroski (homens 18-66, mulheres 18-51)" },
          { v: "slaughter",          l: "Slaughter (crianças, 6 a 17 anos)" },
          { v: "weltman",            l: "Weltman - sem dobra (usa Abdômen)" },
        ],
      },
      // As 9 dobras cutâneas ficam disponíveis pra qualquer protocolo que
      // não seja Weltman/Deurenberg (cada protocolo usa um subconjunto
      // diferente — mostrar todas evita ter que trocar a lista de campos
      // toda vez que o professor muda de protocolo, e o backend só lê as
      // que aquele protocolo específico precisa).
      {
        key: "sf_tricep_mm", label: "Tricipital", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_tricep_mm", f),
      },
      {
        key: "sf_subscapular_mm", label: "Subescapular", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_subscapular_mm", f),
      },
      {
        key: "sf_chest_mm", label: "Peitoral", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_chest_mm", f),
      },
      {
        key: "sf_axillary_mm", label: "Axilar média", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_axillary_mm", f),
      },
      {
        key: "sf_biceps_mm", label: "Bicipital", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_biceps_mm", f),
      },
      {
        key: "sf_suprailiac_mm", label: "Supra-ilíaca", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_suprailiac_mm", f),
      },
      {
        key: "sf_abdominal_mm", label: "Abdominal", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_abdominal_mm", f),
      },
      {
        key: "sf_thigh_mm", label: "Coxa", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_thigh_mm", f),
      },
      {
        key: "sf_calf_mm", label: "Panturrilha (dobra)", unit: "mm",
        showIf: (f) => !["weltman", "deurenberg"].includes(f.skinfold_protocol),
        requiredIf: (f) => isSkinfoldRequired("sf_calf_mm", f),
      },
    ],
  },
  {
    title: "Diâmetros ósseos e sinais vitais",
    subtitle: "Bi-estilóide e Bi-epicôndilo femural alimentam a Massa Óssea; PAS/PAD/FC de repouso são só registro, sem classificação",
    fields: [
      { key: "bone_diam_humeral_cm", label: "Bi-epicôndilo umeral", unit: "cm" },
      { key: "bone_diam_wrist_cm",   label: "Bi-estilóide",         unit: "cm" },
      { key: "bone_diam_femur_cm",   label: "Bi-epicôndilo femural", unit: "cm" },
      { key: "systolic_bp",          label: "PAS",          unit: "mmHg", step: "1" },
      { key: "diastolic_bp",         label: "PAD",          unit: "mmHg",  step: "1" },
      { key: "resting_heart_rate",   label: "FC de repouso", unit: "bpm",  step: "1" },
    ],
  },
];

// ─── Chart series config ─────────────────────────────────────────────────────
// goodDir: +1 = subir é melhora, -1 = cair é melhora, null = depende do contexto
// Função para que themeColors.blue seja lido no render, após o tema do professor estar aplicado.
function getChartSeries() { return [
  { key: "weight",    label: "Peso",             unit: "kg",   color: themeColors.blue, goodDir: null,
    get: m => m.weight_kg },
  { key: "body_fat",  label: "Gordura corporal", unit: "%",    color: "var(--feedback-warning)", goodDir: -1,
    get: m => m.indicators?.body_fat_pct },
  { key: "lean_mass", label: "Massa magra",      unit: "kg",   color: "#9fe1cb", goodDir: +1,
    get: m => m.indicators?.lean_mass_kg },
  { key: "fat_mass",  label: "Massa gorda",      unit: "kg",   color: "#ff6b6b", goodDir: -1,
    get: m => m.indicators?.fat_mass_kg },
  { key: "bmr",       label: "TMB",              unit: "kcal", color: "#a78bfa", goodDir: +1,
    get: m => m.indicators?.bmr_kcal },
  { key: "waist",     label: "Cintura",          unit: "cm",   color: "#fbbf24", goodDir: -1,
    get: m => m.waist_cm },
  { key: "abdomen",   label: "Abdômen",          unit: "cm",   color: "#f472b6", goodDir: -1,
    get: m => m.abdomen_cm },
  { key: "arm",       label: "Braço (D)",        unit: "cm",   color: "var(--feedback-success)", goodDir: +1,
    get: m => m.arm_cm_right },
]; }

// Calcula se a variação foi positiva levando em conta o contexto de cada métrica
function deltaColor(series, delta, lastValue) {
  if (delta === 0) return "var(--text-0-40)";
  let goodDir = series.goodDir;
  // BMI e Peso: direção ideal depende se está acima/abaixo do range saudável
  if (goodDir === null) {
    const bmi = series.key === "bmi" ? lastValue : null; // só usa para BMI/Peso
    if (bmi != null) {
      if (bmi > 25)      goodDir = -1; // acima: reduzir é bom
      else if (bmi < 18.5) goodDir = +1; // abaixo: aumentar é bom
      else return "var(--text-0-40)"; // dentro do range: neutro
    } else {
      return "var(--text-0-40)"; // sem referência: neutro
    }
  }
  const isGood = delta * goodDir > 0;
  return isGood ? "#9fe1cb" : "#ff6b6b";
}

// ─── TimelineChart ────────────────────────────────────────────────────────────
function TimelineChart({ points, unit, color, gradId }) {
  useBodyScrollLock();
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  if (!points || points.length < 2) return null;

  const W = 340, H = 130;
  const PAD = { top: 16, right: 12, bottom: 28, left: 38 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const vals = points.map(p => p.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const range = maxV - minV || 1;

  const px = (i) => PAD.left + (i / (points.length - 1)) * cW;
  const py = (v) => PAD.top + cH - ((v - minV) / range) * cH;

  const polyPts = points.map((p, i) => `${px(i)},${py(p.value)}`).join(" ");
  const areaPts = `${px(0)},${PAD.top + cH} ${polyPts} ${px(points.length - 1)},${PAD.top + cH}`;

  // Pick ~3 evenly-spaced x-axis labels
  const xIdxs = points.length <= 3
    ? points.map((_, i) => i)
    : [0, Math.round((points.length - 1) / 2), points.length - 1];

  // 3 y-axis gridlines
  const yTicks = [0, 0.5, 1].map(t => ({ t, v: (minV + t * range) }));

  function handleMouseMove(e) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = W / rect.width;
    const mx = (e.clientX - rect.left) * scaleX;
    // find closest point
    let closest = 0, minDx = Infinity;
    points.forEach((_, i) => { const dx = Math.abs(px(i) - mx); if (dx < minDx) { minDx = dx; closest = i; } });
    setTooltip({ i: closest, x: px(closest), y: py(points[closest].value) });
  }

  const tt = tooltip ? points[tooltip.i] : null;

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: "block", overflow: "visible", cursor: "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid + Y labels */}
        {yTicks.map(({ t, v }) => {
          const y = PAD.top + cH - t * cH;
          return (
            <g key={t}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                    stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <text x={PAD.left - 4} y={y + 3.5} textAnchor="end"
                    fontSize="9" fill="var(--text-0-40)">{v.toFixed(1)}</text>
            </g>
          );
        })}

        {/* Area */}
        <polygon points={areaPts} fill={`url(#${gradId})`} />

        {/* Line */}
        <polyline points={polyPts} fill="none" stroke={color} strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots */}
        {points.map((p, i) => (
          <circle key={i} cx={px(i)} cy={py(p.value)} r="3.5"
                  fill={tooltip?.i === i ? color : "var(--surface-0-98)"}
                  stroke={color} strokeWidth="1.8" />
        ))}

        {/* Tooltip crosshair */}
        {tooltip && (
          <line x1={tooltip.x} y1={PAD.top} x2={tooltip.x} y2={PAD.top + cH}
                stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
        )}

        {/* X axis labels */}
        {xIdxs.map(i => (
          <text key={i} x={px(i)} y={H - 5} textAnchor="middle"
                fontSize="9" fill="var(--text-0-40)">{points[i].label}</text>
        ))}
      </svg>

      {/* Tooltip bubble */}
      {tt && (
        <div style={{
          position: "absolute",
          left: `${(tooltip.x / W) * 100}%`,
          top: `${(tooltip.y / H) * 100}%`,
          transform: "translate(-50%, -130%)",
          background: "rgba(10,18,38,0.96)",
          border: `1px solid ${color}40`,
          borderRadius: 8,
          padding: "4px 10px",
          fontSize: 12,
          fontWeight: 700,
          color: "var(--text-primary)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          zIndex: 20,
          boxShadow: `0 4px 16px rgba(0,0,0,0.4)`,
        }}>
          {typeof tt.value === "number" ? tt.value.toFixed(2) : tt.value}{unit ? ` ${unit}` : ""}
          <div style={{ fontWeight: 400, fontSize: 10, color: "var(--text-0-50)", marginTop: 1 }}>{tt.label}</div>
        </div>
      )}
    </div>
  );
}

// ─── TrendLine mini SVG ──────────────────────────────────────────────────────
function TrendLine({ values, color = themeColors.blue }) {
  if (!values || values.length < 2) return null;
  const W = 80, H = 32;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function BodyMeasurementsModal({ open, onClose, apiUrl = "", currentUser = null }) {
  const base = apiUrl.replace(/\/+$/, "");

  // Professor/admin: pode gerenciar as próprias medidas OU as de um aluno
  // vinculado — mesmo padrão de NutritionModal (seletor "Você" / lista de
  // alunos). Aluno nunca vê o seletor, sempre gerencia só as próprias.
  const isManager = currentUser?.role === "professor" || currentUser?.role === "admin";
  // IMC é dado da avaliação do professor: o aluno não vê (o backend também não envia)
  const hideBmi = !isManager;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(""); // "" = você mesmo

  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // R365: modal é desmontado/remontado a cada abertura (Dashboard.js só renderiza
  // quando openBodyMeasurements é true), então o initializer roda de novo toda vez
  // e já chega com o autofill de "você mesmo" pronto — sem precisar de useEffect.
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...autofillFromProfile(currentUser) }));
  const photoGuideFile = form.gender === "F" ? "guia-fotos-progresso-fem.jpg" : "guia-fotos-progresso-masc.jpg";
  const photoGuideUrl  = `${base}/uploads/${photoGuideFile}`;
  const [photoGuideOpen, setPhotoGuideOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("form"); // "form" | "history" | "evolucao"
  const [detailId, setDetailId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [skinfoldsExpanded, setSkinfoldsExpanded] = useState(false); // R363: seção opcional/avançada, some por padrão

  // Prévia ao vivo dos indicadores calculados (R362) — recalcula a cada mudança no form.
  const previewIndicators = buildIndicators(computeIndicatorsClient(form), hideBmi);

  // Aluno abre direto no Histórico (a medida nova é feita pelo professor na avaliação);
  // professor/admin continua abrindo em "Nova medida".
  useEffect(() => {
    if (open && !isManager) setTab("history");
  }, [open, isManager]);

  useEffect(() => {
    if (open && isManager) {
      api.get("/users/students", { params: { scope: "org", limit: 500 } })
        .then(res => setStudents(res.data || []))
        .catch(() => setStudents([]));
    }
  }, [open, isManager]);

  const fetchMeasurements = useCallback(async (studentId) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = studentId ? `/body-measurements/student/${studentId}` : "/body-measurements";
      const res = await api.get(endpoint);
      setMeasurements(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar medidas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchMeasurements(selectedStudentId || null);
  }, [open, selectedStudentId, fetchMeasurements]);

  // Trocar de aluno: volta pro form limpo e histórico, evita ficar editando
  // a medida de uma pessoa enquanto o form mostra outra. Usa o `id` recebido
  // (não o selectedStudentId do state, que só atualiza no próximo render) pra
  // já montar o autofill (R365) da pessoa certa.
  function handleStudentChange(id) {
    setSelectedStudentId(id);
    setEditId(null);
    const target = id ? students.find(s => String(s.id) === String(id)) : currentUser;
    setForm({ ...EMPTY_FORM, ...autofillFromProfile(target) });
    setDetailId(null);
    setTab("history");
  }

  function startEdit(m) {
    setEditId(m.id);
    setForm({
      date: m.date,
      height_cm: m.height_cm ?? "",
      weight_kg: m.weight_kg ?? "",
      age: m.age ?? "",
      gender: m.gender ?? "",
      waist_cm: m.waist_cm ?? "",
      abdomen_cm: m.abdomen_cm ?? "",
      hip_cm: m.hip_cm ?? "",
      neck_cm: m.neck_cm ?? "",
      chest_cm: m.chest_cm ?? "",
      arm_cm_right: m.arm_cm_right ?? "",
      arm_cm_left: m.arm_cm_left ?? "",
      forearm_cm_right: m.forearm_cm_right ?? "",
      forearm_cm_left: m.forearm_cm_left ?? "",
      thigh_cm_right: m.thigh_cm_right ?? "",
      thigh_cm_left: m.thigh_cm_left ?? "",
      calf_cm_right: m.calf_cm_right ?? "",
      calf_cm_left: m.calf_cm_left ?? "",
      skinfold_protocol: m.skinfold_protocol ?? "",
      sf_tricep_mm: m.sf_tricep_mm ?? "",
      sf_subscapular_mm: m.sf_subscapular_mm ?? "",
      sf_chest_mm: m.sf_chest_mm ?? "",
      sf_axillary_mm: m.sf_axillary_mm ?? "",
      sf_suprailiac_mm: m.sf_suprailiac_mm ?? "",
      sf_abdominal_mm: m.sf_abdominal_mm ?? "",
      sf_thigh_mm: m.sf_thigh_mm ?? "",
      sf_biceps_mm: m.sf_biceps_mm ?? "",
      sf_calf_mm: m.sf_calf_mm ?? "",
    });
    setTab("form");
    setDetailId(null);
  }

  function resetForm() {
    setEditId(null);
    const target = selectedStudentId ? students.find(s => String(s.id) === String(selectedStudentId)) : currentUser;
    setForm({ ...EMPTY_FORM, ...autofillFromProfile(target) });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === "" || v === null || v === undefined) { body[k] = null; continue; }
      if (k === "date" || k === "gender") { body[k] = v; continue; }
      body[k] = parseFloat(v);
    }
    body.date = form.date;
    body.gender = form.gender || null;
    body.age = form.age !== "" ? parseInt(form.age) : null;
    if (selectedStudentId) body.for_user_id = Number(selectedStudentId);

    try {
      const res = editId
        ? await api.put(`/body-measurements/${editId}`, body)
        : await api.post("/body-measurements", body);
      const saved = res.data;
      if (editId) {
        setMeasurements(prev => prev.map(m => m.id === editId ? saved : m));
        setDetailId(saved.id);
        setTab("history");
      } else {
        setMeasurements(prev => [saved, ...prev]);
        setDetailId(saved.id);
        setTab("history");
      }
      resetForm();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/body-measurements/${id}`);
      setMeasurements(prev => prev.filter(m => m.id !== id));
      if (detailId === id) setDetailId(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao excluir");
    } finally {
      setDeleteConfirm(null);
    }
  }

  if (!open) return null;

  const detail = measurements.find(m => m.id === detailId) || measurements[0] || null;
  const indicators = detail ? buildIndicators(detail.indicators, hideBmi) : [];

  // Trend data — last 8 entries reversed (oldest→newest) for mini-charts
  const trend = [...measurements].reverse().slice(-8);
  const weightTrend = trend.map(m => m.weight_kg).filter(Boolean);
  const bmiTrend = trend.map(m => m.indicators?.bmi).filter(Boolean);

  // All measurements oldest→newest for Evolução tab
  const allChron = [...measurements].reverse();

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}><FontAwesomeIcon icon={faRulerCombined} /> Medidas Corporais</h2>
          {tab === "fotos" && (
            <button
              key={tab}
              type="button"
              onClick={() => setPhotoGuideOpen(true)}
              className={styles.headerCameraGuide}
              title="Ver guia de como tirar as fotos"
            >
              <FontAwesomeIcon icon={faCamera} />
              <span> Como tirar as fotos?</span>
            </button>
          )}
        </div>
        <PhotoGuideModal open={photoGuideOpen} onClose={() => setPhotoGuideOpen(false)} imageUrl={photoGuideUrl} />

        {/* Seletor de aluno — só professor/admin */}
        {isManager && students.length > 0 && (
          <div className={styles.studentSelectorRow}>
            <FontAwesomeIcon icon={faUser} className={styles.studentSelectorIcon} />
            <select
              className={styles.studentSelect}
              value={selectedStudentId}
              onChange={e => handleStudentChange(e.target.value)}
            >
              <option value="">Você mesmo</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === "form" ? styles.tabActive : ""}`}
            onClick={() => setTab("form")}
          >
            {editId ? <><FontAwesomeIcon icon={faPenToSquare} /> Editar medida</> : <><FontAwesomeIcon icon={faPlus} /> Nova medida</>}
          </button>
          <button
            className={`${styles.tab} ${tab === "history" ? styles.tabActive : ""}`}
            onClick={() => setTab("history")}
          >
            <FontAwesomeIcon icon={faTableList} /> Histórico{measurements.length > 0 && <span className={styles.tabBadge}>{measurements.length}</span>}
          </button>
          <button
            className={`${styles.tab} ${tab === "evolucao" ? styles.tabActive : ""}`}
            onClick={() => setTab("evolucao")}
          >
            <FontAwesomeIcon icon={faChartLine} /> Evolução
          </button>
          <button
            className={`${styles.tab} ${tab === "fotos" ? styles.tabActive : ""}`}
            onClick={() => setTab("fotos")}
          >
            <FontAwesomeIcon icon={faCamera} /> Fotos
          </button>
        </div>

        {error && <div className={styles.errorBox}>{error}</div>}

        {/* ── FORM TAB ─────────────────────────────────────────────────────── */}
        {tab === "form" && (
          <form className={styles.form} onSubmit={handleSubmit}>
            {FIELD_GROUPS.map(group => {
              // R363: dobras cutâneas é um passo avançado/opcional (a maioria dos
              // usuários não vai medir com adipômetro) — some por padrão pra não
              // intimidar quem só quer registrar peso/circunferências. Mas se o
              // registro sendo editado já tem dobras preenchidas, mostra sempre —
              // nunca esconder dado que a pessoa já tinha informado.
              const isCollapsible = !!group.collapsible;
              const groupHasData = isCollapsible && group.fields.some(f => form[f.key] !== "");
              const expanded = !isCollapsible || skinfoldsExpanded || groupHasData;
              return (
                <div key={group.title} className={styles.formGroup}>
                  <div
                    className={`${styles.formGroupTitle} ${isCollapsible ? styles.formGroupTitleToggle : ""}`}
                    onClick={isCollapsible ? () => setSkinfoldsExpanded(v => !v) : undefined}
                    role={isCollapsible ? "button" : undefined}
                    tabIndex={isCollapsible ? 0 : undefined}
                    onKeyDown={isCollapsible ? (e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSkinfoldsExpanded(v => !v); } }) : undefined}
                  >
                    {group.title}
                    {isCollapsible && <span className={styles.optionalBadge}>Opcional</span>}
                    {isCollapsible && (
                      <FontAwesomeIcon icon={faChevronDown} className={`${styles.groupChevron} ${expanded ? styles.groupChevronOpen : ""}`} />
                    )}
                  </div>
                  {group.subtitle && expanded && <div className={styles.formGroupSubtitle}>{group.subtitle}</div>}
                  {expanded && (
                    <div className={styles.formRow}>
                      {group.fields.filter(f => !f.showIf || f.showIf(form)).map(f => {
                        const isRequired = !!f.required || (f.requiredIf ? f.requiredIf(form) : false);
                        return (
                        <div key={f.key} className={`${styles.formField} ${f.type === "date" ? styles.formFieldWide : ""}`}>
                          <label className={styles.fieldLabel}>
                            {f.label}
                            {isRequired && <span className={styles.requiredMark} aria-hidden="true"> *</span>}
                          </label>
                          {f.type === "select" ? (
                            <select
                              className={`${styles.fieldInput} ${styles.fieldSelect}`}
                              value={form[f.key]}
                              onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                            >
                              {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                          ) : f.type === "date" ? (
                            <SplitDateInput
                              value={form[f.key]}
                              onChange={v => setForm(prev => ({ ...prev, [f.key]: v }))}
                              inputClassName={styles.fieldInput}
                            />
                          ) : (
                            <div className={styles.fieldInputWrap}>
                              <input
                                className={styles.fieldInput}
                                type={f.type || "number"}
                                step={f.step || "0.1"}
                                required={isRequired}
                                aria-required={isRequired}
                                value={form[f.key]}
                                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                              />
                              {f.unit && <span className={styles.fieldUnit}>{f.unit}</span>}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* R362: prévia ao vivo — mesma UI do histórico (indicatorGrid/Card),
                calculada no cliente enquanto a pessoa digita, sem persistir nada. */}
            {previewIndicators.length > 0 ? (
              <div className={styles.previewSection}>
                <div className={styles.sectionLabel}>Prévia dos indicadores</div>
                <div className={styles.indicatorGrid}>
                  {previewIndicators.map(ind => (
                    <div key={ind.label} className={`${styles.indicatorCard} ${ind.wide ? styles.indicatorWide : ""}`}>
                      <div className={styles.indicatorLabelRow}>
                        <div className={styles.indicatorLabel}>{ind.label}</div>
                        {ind.tooltip && (
                          <div className={styles.infoTooltipWrap}>
                            <button
                              type="button"
                              className={styles.infoTooltipTrigger}
                              aria-label="Observação sobre este indicador"
                              aria-describedby={`indicatorTooltip-${ind.label}`}
                            >
                              <FontAwesomeIcon icon={faCircleInfo} />
                            </button>
                            <div className={styles.infoTooltipBubble} role="tooltip" id={`indicatorTooltip-${ind.label}`}>
                              {ind.tooltip}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className={styles.indicatorValue} style={{ color: ind.riskColor }}>
                        {ind.value}<span className={styles.indicatorUnit}>{ind.unit}</span>
                      </div>
                      {ind.risk && <div className={styles.indicatorRisk} style={{ color: ind.riskColor }}>{ind.risk}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.previewHint}>Preencha peso e altura para ver os indicadores calculados automaticamente.</div>
            )}

            <div className={styles.formActions}>
              {editId && (
                <button type="button" className={styles.cancelBtn} onClick={() => { resetForm(); setTab("history"); }}>
                  Cancelar
                </button>
              )}
              <button type="submit" className={styles.saveBtn} disabled={saving}>
                {saving ? "Salvando…" : editId ? "Salvar alterações" : "Salvar medidas"}
              </button>
            </div>
          </form>
        )}

        {/* ── HISTORY TAB ──────────────────────────────────────────────────── */}
        {tab === "history" && (
          <div className={styles.historyWrap}>
            {loading && <div className={styles.loadingState}>Carregando…</div>}
            {!loading && measurements.length === 0 && (
              <div className={styles.emptyState}>Nenhuma medida registrada ainda.<br />Use a aba "Nova medida" para começar.</div>
            )}

            {!loading && measurements.length > 0 && (
              <div className={styles.historyLayout}>

                {/* Left: list */}
                <div className={styles.historyList}>
                  {measurements.map(m => {
                    const isActive = detail?.id === m.id;
                    const bmi = m.indicators?.bmi;
                    const bf = m.indicators?.body_fat_pct;
                    return (
                      <button
                        key={m.id}
                        className={`${styles.historyItem} ${isActive ? styles.historyItemActive : ""}`}
                        onClick={() => setDetailId(m.id)}
                      >
                        <div className={styles.historyItemDate}>{fmtDate(m.date)}</div>
                        <div className={styles.historyItemStats}>
                          {m.weight_kg && <span className={styles.historyChip}>{m.weight_kg} kg</span>}
                          {bmi && !hideBmi && <span className={styles.historyChip}>IMC {bmi}</span>}
                          {bf != null && <span className={styles.historyChip}>{bf}% gord.</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Right: detail */}
                {detail && (
                  <div className={styles.detailPanel}>
                    <div className={styles.detailHeader}>
                      <div className={styles.detailDate}>{fmtDate(detail.date)}</div>
                      <div className={styles.detailActions}>
                        {isManager && (
                          <button
                            className={styles.editBtn}
                            disabled={pdfBusy}
                            title="Baixar a avaliação em PDF com a sua marca (sem IMC)"
                            onClick={async () => {
                              setPdfBusy(true);
                              try { await downloadPdf(api, `/body-measurements/${detail.id}/pdf`, "avaliacao.pdf"); }
                              catch (e) { setError("Não foi possível gerar o PDF da avaliação. Tente novamente."); }
                              finally { setPdfBusy(false); }
                            }}
                          >
                            <FontAwesomeIcon icon={faFilePdf} /> {pdfBusy ? "Gerando..." : "PDF"}
                          </button>
                        )}
                        <button className={styles.editBtn} onClick={() => startEdit(detail)}><FontAwesomeIcon icon={faPenToSquare} /> Editar</button>
                        <button className={styles.deleteBtn} onClick={() => setDeleteConfirm(detail.id)}><FontAwesomeIcon icon={faTrashCan} /></button>
                      </div>
                    </div>

                    {/* Trend mini-charts */}
                    {(weightTrend.length > 1 || bmiTrend.length > 1) && (
                      <div className={styles.trendsRow}>
                        {weightTrend.length > 1 && (
                          <div className={styles.trendCard}>
                            <div className={styles.trendLabel}>Peso</div>
                            <TrendLine values={weightTrend} color={themeColors.blue} />
                            <div className={styles.trendRange}>
                              {Math.min(...weightTrend).toFixed(1)} – {Math.max(...weightTrend).toFixed(1)} kg
                            </div>
                          </div>
                        )}
                        {!hideBmi && bmiTrend.length > 1 && (
                          <div className={styles.trendCard}>
                            <div className={styles.trendLabel}>IMC</div>
                            <TrendLine values={bmiTrend} color="#63b3ed" />
                            <div className={styles.trendRange}>
                              {Math.min(...bmiTrend).toFixed(1)} – {Math.max(...bmiTrend).toFixed(1)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Indicators */}
                    {indicators.length > 0 && (
                      <>
                        <div className={styles.sectionLabel}>Indicadores calculados</div>
                        <div className={styles.indicatorGrid}>
                          {indicators.map((ind, i) => (
                            <div key={i} className={`${styles.indicatorCard} ${ind.wide ? styles.indicatorWide : ""}`}>
                              <div className={styles.indicatorLabelRow}>
                                <div className={styles.indicatorLabel}>{ind.label}</div>
                                {ind.tooltip && (
                                  <div className={styles.infoTooltipWrap}>
                                    <button
                                      type="button"
                                      className={styles.infoTooltipTrigger}
                                      aria-label="Observação sobre este indicador"
                                      aria-describedby={`indicatorTooltipHist-${i}`}
                                    >
                                      <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>
                                    <div className={styles.infoTooltipBubble} role="tooltip" id={`indicatorTooltipHist-${i}`}>
                                      {ind.tooltip}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className={styles.indicatorValue} style={{ color: ind.riskColor }}>
                                {ind.value}{ind.unit ? <span className={styles.indicatorUnit}> {ind.unit}</span> : ""}
                              </div>
                              {ind.risk && <div className={styles.indicatorRisk} style={{ color: ind.riskColor }}>{ind.risk}</div>}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Assimetria de membros (D/E) — Frente A da Avaliação Física v2 */}
                    {detail.indicators?.limb_asymmetry?.length > 0 && (
                      <>
                        <div className={styles.sectionLabel}>Assimetria entre lados</div>
                        <div className={styles.asymmetryGrid}>
                          {detail.indicators.limb_asymmetry.map((a) => (
                            <div
                              key={a.limb}
                              className={`${styles.asymmetryCard} ${a.significant ? styles.asymmetrySignificant : ""}`}
                            >
                              <div>
                                <div className={styles.asymmetryLimb}>{a.limb}</div>
                                <div className={styles.asymmetrySides}>D {a.right_cm}cm · E {a.left_cm}cm</div>
                              </div>
                              <div className={styles.asymmetryDiff}>
                                {a.diff_cm}cm{a.significant ? " · assimetria" : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Raw measurements */}
                    <div className={styles.sectionLabel}>Medidas registradas</div>
                    <div className={styles.rawGrid}>
                      {[
                        ["Peso", detail.weight_kg, "kg"],
                        ["Altura", detail.height_cm, "cm"],
                        ["Idade", detail.age, "anos"],
                        ["Sexo", detail.gender === "M" ? "Masculino" : detail.gender === "F" ? "Feminino" : null, ""],
                        ["Cintura", detail.waist_cm, "cm"],
                        ["Abdômen", detail.abdomen_cm, "cm"],
                        ["Quadril", detail.hip_cm, "cm"],
                        ["Pescoço", detail.neck_cm, "cm"],
                        ["Peito", detail.chest_cm, "cm"],
                        ["Braço (D)", detail.arm_cm_right, "cm"],
                        ["Braço (E)", detail.arm_cm_left, "cm"],
                        ["Antebraço (D)", detail.forearm_cm_right, "cm"],
                        ["Antebraço (E)", detail.forearm_cm_left, "cm"],
                        ["Coxa (D)", detail.thigh_cm_right, "cm"],
                        ["Coxa (E)", detail.thigh_cm_left, "cm"],
                        ["Panturrilha (D)", detail.calf_cm_right, "cm"],
                        ["Panturrilha (E)", detail.calf_cm_left, "cm"],
                        ["Bi-epicôndilo umeral", detail.bone_diam_humeral_cm, "cm"],
                        ["Bi-estilóide", detail.bone_diam_wrist_cm, "cm"],
                        ["Bi-epicôndilo femural", detail.bone_diam_femur_cm, "cm"],
                        ["PAS", detail.systolic_bp, "mmHg"],
                        ["PAD", detail.diastolic_bp, "mmHg"],
                        ["FC de repouso", detail.resting_heart_rate, "bpm"],
                      ].filter(([, v]) => v != null).map(([label, val, unit]) => (
                        <div key={label} className={styles.rawItem}>
                          <span className={styles.rawLabel}>{label}</span>
                          <span className={styles.rawValue}>{val}{unit ? <span className={styles.rawUnit}> {unit}</span> : ""}</span>
                        </div>
                      ))}
                    </div>

                    {/* Delete confirmation */}
                    {deleteConfirm === detail.id && (
                      <div className={styles.deleteConfirmBox}>
                        <span>Excluir esta medida?</span>
                        <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(detail.id)}>Excluir</button>
                        <button className={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancelar</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* ── EVOLUÇÃO TAB ─────────────────────────────────────────────────── */}
        {tab === "evolucao" && (
          <div className={styles.evolucaoWrap}>
            {allChron.length < 2 && (
              <div className={styles.emptyState}>
                Registre pelo menos 2 medidas para ver a evolução ao longo do tempo.
              </div>
            )}
            {allChron.length >= 2 && (
              <div className={styles.chartsGrid}>
                {getChartSeries().map(s => {
                  const pts = allChron
                    .map(m => ({ value: s.get(m), label: fmtDate(m.date) }))
                    .filter(p => p.value != null);
                  if (pts.length < 2) return null;
                  const first = pts[0].value;
                  const last  = pts[pts.length - 1].value;
                  const delta = last - first;
                  const pct   = first !== 0 ? ((delta / first) * 100) : 0;
                  const dColor = deltaColor(s, delta, last);
                  return (
                    <div key={s.key} className={styles.chartCard}>
                      <div className={styles.chartCardHeader}>
                        <span className={styles.chartCardTitle}>{s.label}</span>
                        <span className={styles.chartCardDelta} style={{ color: dColor }}>
                          {delta >= 0 ? "+" : ""}{delta.toFixed(1)}{s.unit ? ` ${s.unit}` : ""}
                          {" "}
                          <span style={{ fontSize: 10, opacity: 0.7 }}>
                            ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <TimelineChart
                        points={pts}
                        unit={s.unit}
                        color={s.color}
                        gradId={`evol-${s.key}`}
                      />
                      {/* Footer: valor inicial → valor atual (datas já estão no eixo X) */}
                      <div className={styles.chartCardFooter}>
                        <span style={{ color: "var(--text-0-40)" }}>
                          {first.toFixed(1)}{s.unit ? ` ${s.unit}` : ""}
                        </span>
                        <span style={{ color: "var(--text-0-40)", fontSize: 10 }}>→</span>
                        <span style={{ fontWeight: 700, color: s.color }}>
                          {last.toFixed(1)}{s.unit ? ` ${s.unit}` : ""}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── FOTOS TAB ────────────────────────────────────────────────────── */}
        {tab === "fotos" && (
          <div className={styles.evolucaoWrap}>
            <BodyProgressContent
              apiBase={base}
              studentId={selectedStudentId || null}
              canManage={isManager}
            />
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
